import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  REGIONAL_PLAN_DECISION_TOAST_MESSAGES,
  RegionalPlanApprovalStatus,
  RegionalPlanNewElevationRequestStatus,
} from 'src/constants/regionalPlanApprovals.constants'
import { TOAST_VARIANTS } from 'src/constants/toast.constants'
import { RootState } from 'src/redux/stores/store'
import {
  ApprovalData,
  RegionalApprovalRow,
  RegionalPlanNewElevationRequestData,
} from 'src/types/plan.interface'
import {
  isRegionalApproval,
  isRegionalPlanNewElevationRequest,
} from 'src/utils/regionalApprovalType.util'

import {
  createRegionalApprovalDecisionNotification,
  createRegionalApprovalRequestNotification,
  createRegionalElevationAddedNotification,
  createRegionalNewElevationCompletedNotification,
  createRegionalNewElevationRequestNotification,
} from '../toastNotification/NotificationMessages'
import { showToast } from '../toastNotification/toastNotification'

type RegionalApprovalMessage = ApprovalData & RegionalApprovalRow

// Emails and business regions are compared case-insensitively: the values on an
// approval document come from a different service and are not normalised there.
const isSameText = (
  value: string | undefined,
  other: string | undefined
): boolean =>
  Boolean(value) &&
  Boolean(other) &&
  value?.toLowerCase() === other?.toLowerCase()

/**
 * Announces both sides of a regional plan copy approval (LTGC-5711): the
 * decision on this user's own request, and a new request arriving for an
 * approver who owns the region it belongs to. New-elevation requests ride the
 * same channel: the requester hears each time the plan gains elevations and
 * when the lead marks the request complete, the lead hears when a request is
 * filed in a region they own.
 *
 * The approvals change feed broadcasts every approvalsV2 document to every
 * connected client, so this listener decides per document which — if either —
 * announcement it earns, and toasts each document at most once per session: a
 * snapshot load, a replayed message, or a rebroadcast of an already-announced
 * document must stay silent. Mounted at the app root inside the router so the
 * toasts can link to the draft and to the approvals queue.
 */
const RegionalApprovalNotificationListener: React.FC = () => {
  const navigate = useNavigate()

  const userEmail: string | undefined = useSelector(
    (state: RootState) => state.Auth?.userInfo?.email
  )

  const approvalMessages = useSelector(
    (state: RootState) => state.signalR?.approvalMessages
  )

  // The user's own requests as last fetched (view: 'mine'). Read only to record
  // where each request stood when this session started — never to toast.
  const requestedApprovals = useSelector(
    (state: RootState) => state.plans?.myRegionalApprovals?.data
  )

  // The approver queue as last fetched (view: 'queue'), read for the same
  // reason: everything already waiting here predates this session.
  const queuedApprovals = useSelector(
    (state: RootState) => state.plans?.regionalApprovals?.data
  )

  const isRegionalApprover = useSelector(
    (state: RootState) => state.plans?.isCurrentUserARegionalApprover
  )

  const approverRegions = useSelector(
    (state: RootState) => state.plans?.regionalPlanApproverRegions
  )

  // Statuses already announced (or first seen as decided), keyed by document id,
  // so a decision is toasted exactly once per session.
  const announcedStatusesRef = useRef(new Map<string, string>())

  // The elevations each new-elevation request was last seen carrying in
  // addedElevations, keyed by document id, so only the ones that have just
  // appeared on the plan are announced and a rebroadcast stays quiet.
  const announcedAddedElevationsRef = useRef(new Map<string, string[]>())

  useEffect(() => {
    const ownedRegions = new Set(
      approverRegions?.map((region) => region?.toLowerCase()) ?? []
    )

    const isOwnRequest = (approval: RegionalApprovalMessage): boolean =>
      isSameText(approval.userId, userEmail)

    // An approver only hears about the regions they own, so an empty or missing
    // region list announces nothing rather than everything.
    const isRequestForThisApprover = (
      approval: RegionalApprovalMessage
    ): boolean =>
      isRegionalApprover === true &&
      !isOwnRequest(approval) &&
      ownedRegions.has(approval.businessRegion?.toLowerCase())

    const rememberStatus = (approval: RegionalApprovalRow): void => {
      if (!announcedStatusesRef.current.has(approval.id)) {
        announcedStatusesRef.current.set(approval.id, approval.status)
      }
      if (
        isRegionalPlanNewElevationRequest(approval) &&
        !announcedAddedElevationsRef.current.has(approval.id)
      ) {
        announcedAddedElevationsRef.current.set(
          approval.id,
          approval.addedElevations ?? []
        )
      }
    }

    // The elevations a new-elevation request carries that this session has not
    // announced yet, recording the full set as now seen.
    const takeNewlyAddedElevations = (
      approval: RegionalPlanNewElevationRequestData
    ): string[] => {
      const previouslySeen =
        announcedAddedElevationsRef.current.get(approval.id) ?? []
      const currentlyAdded = approval.addedElevations ?? []
      announcedAddedElevationsRef.current.set(approval.id, currentlyAdded)
      return currentlyAdded.filter(
        (elevation) => !previouslySeen.includes(elevation)
      )
    }

    // Toasts the outcome of this user's own request once it leaves pending: a
    // decision on a copy request, or the lead marking a new-elevation request
    // complete (legacy requests left pending by the change feed as fulfilled).
    const announceOwnOutcome = (approval: RegionalApprovalMessage): void => {
      if (isRegionalPlanNewElevationRequest(approval)) {
        const isClosed =
          approval.status === RegionalPlanNewElevationRequestStatus.COMPLETED ||
          approval.status === RegionalPlanNewElevationRequestStatus.FULFILLED
        if (!isClosed) {
          return
        }
        showToast({
          message: createRegionalNewElevationCompletedNotification(approval),
          type: TOAST_VARIANTS.SUCCESS,
        })
        return
      }

      const message =
        REGIONAL_PLAN_DECISION_TOAST_MESSAGES[
          approval.status as Exclude<
            RegionalPlanApprovalStatus,
            RegionalPlanApprovalStatus.PENDING
          >
        ]
      if (!message) {
        return
      }

      showToast({
        message: createRegionalApprovalDecisionNotification(
          message,
          approval.planName,
          approval.draftPlanId
        ),
        type:
          approval.status === RegionalPlanApprovalStatus.DECLINED
            ? TOAST_VARIANTS.WARNING
            : TOAST_VARIANTS.SUCCESS,
      })
    }

    // Seed from the fetched snapshots before reading the broadcasts.
    // announcedStatusesRef is per-session and a decision only toasted when this
    // same session had already seen that request PENDING — so a requester who
    // reloaded, which is the normal sequence (request approval, come back
    // later), got no toast at all: their session's first sight of the request
    // was the decision itself. Seeding records the status each request was in
    // at load, which restores the pending→decided transition across a reload
    // while keeping a replayed message for an already-decided request silent.
    requestedApprovals?.filter(isRegionalApproval).forEach(rememberStatus)

    // The approver's queue gets seeded for the mirror-image reason: every
    // request already sitting in it was made before this session started, so it
    // is not a new arrival and a rebroadcast of it must not toast.
    queuedApprovals?.filter(isRegionalApproval).forEach(rememberStatus)

    approvalMessages?.filter(isRegionalApproval).forEach((approval) => {
      const previousStatus = announcedStatusesRef.current.get(approval.id)
      announcedStatusesRef.current.set(approval.id, approval.status)

      // A document is either this user's own request or somebody else's — never
      // both — so the two announcements below can never fire for the same one.
      if (isOwnRequest(approval)) {
        const hasJustLeftPending =
          approval.status !== RegionalPlanApprovalStatus.PENDING &&
          previousStatus === RegionalPlanApprovalStatus.PENDING

        // A new-elevation request also reports the elevations the plan gained
        // while it waits. Each batch is announced once; the completion toast
        // takes precedence when both arrive on the same broadcast.
        const newlyAddedElevations = isRegionalPlanNewElevationRequest(approval)
          ? takeNewlyAddedElevations(approval)
          : []

        if (hasJustLeftPending) {
          announceOwnOutcome(approval)
        } else if (
          approval.status === RegionalPlanApprovalStatus.PENDING &&
          newlyAddedElevations.length > 0
        ) {
          showToast({
            message: createRegionalElevationAddedNotification(
              approval,
              newlyAddedElevations
            ),
            type: TOAST_VARIANTS.SUCCESS,
          })
        }
        return
      }

      // A document that has just BECOME pending is waiting on this approver —
      // whether that is a first request or a division re-requesting after
      // editing a plan that had already been decided. Requiring the document
      // to be previously unseen missed that second case entirely: the session
      // already held it as approved, so the re-request arrived silently.
      //
      // A rebroadcast of something already pending stays quiet, which is what
      // keeps replays and the queue snapshot from toasting.
      const hasJustArrived =
        approval.status === RegionalPlanApprovalStatus.PENDING &&
        previousStatus !== RegionalPlanApprovalStatus.PENDING

      if (!hasJustArrived || !isRequestForThisApprover(approval)) {
        return
      }

      showToast({
        message: isRegionalPlanNewElevationRequest(approval)
          ? createRegionalNewElevationRequestNotification(approval, navigate)
          : createRegionalApprovalRequestNotification(approval, navigate),
        // Nothing has succeeded for this user and nothing has failed: a request
        // is waiting on them. Of the three variants the toast offers, WARNING is
        // the one that reads as "needs attention" — SUCCESS would celebrate
        // somebody else's action and ERROR would report a routine request as a
        // failure.
        type: TOAST_VARIANTS.WARNING,
      })
    })
  }, [
    approvalMessages,
    approverRegions,
    isRegionalApprover,
    navigate,
    queuedApprovals,
    requestedApprovals,
    userEmail,
  ])

  return null
}

export default RegionalApprovalNotificationListener
